/**
 * 支付失败引导：从结果页回 Home 恢复收银台（单商品 / 购物车）
 */

import globalConfig from '@utiles/officialStore/globalConfig';
import { getGlobalGetGotoLink } from '@utiles/globalGetLink';

const mainHost = globalConfig.getMainHost();

/** 单品失败 tip「切换支付方式」回跳标记 */
export const PAY_FAIL_RESUME_SWITCH_PAYTYPE = 'switchPaytype';
/** 购物车失败 tip「切换支付方式」回跳标记 */
export const PAY_FAIL_RESUME_CART_SWITCH = 'cartSwitch';

const PAY_FAIL_RESUME_URL_KEYS = ['payResume', 'payCartOrderId'];

/** 是否由结果页失败 tip「切换支付方式」回跳进入 Home */
export function isPayFailSwitchPaytypeEntry() {
  const payResume = globalConfig.getHrefParam('payResume');
  return (
    payResume === PAY_FAIL_RESUME_SWITCH_PAYTYPE ||
    payResume === PAY_FAIL_RESUME_CART_SWITCH
  );
}

/** 清理回跳 URL 参数，避免刷新重复触发恢复/打点 */
export function consumePayFailSwitchPaytypeResume() {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    PAY_FAIL_RESUME_URL_KEYS.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });
    if (changed) {
      const next =
        url.pathname +
        (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') +
        url.hash;
      window.history.replaceState(null, '', next);
    }
  } catch (err) {
    console.warn('[PayFailSwitch] clear URL failed', err);
  }
}

/**
 * @typedef {Object} PayFailHomeContext
 * @property {string|number} [gameId]
 * @property {string} [cid]
 * @property {string} [scid]
 * @property {string} [roleId]
 * @property {string} [serverId]
 * @property {string} [mode]
 * @property {string} [roleName]
 * @property {string|number} [productId]
 * @property {string|number} [cpProductId]
 * @property {string} [location]
 * @property {boolean} [isCart]
 * @property {string|number} [orderId]
 * @property {boolean} [isJpPlatform]
 */

/**
 * @param {PayFailHomeContext} ctx
 * @returns {string}
 */
export function buildPayFailHomeQuery(ctx = {}) {
  const query = new URLSearchParams();
  const fields = {
    gameId: ctx.gameId,
    cid: ctx.cid,
    scid: ctx.scid,
    roleId: ctx.roleId,
    serverId: ctx.serverId,
    mode: ctx.mode,
    roleName: ctx.roleName,
    productId: ctx.productId,
    cpProductId: ctx.cpProductId,
  };
  Object.keys(fields).forEach((key) => {
    const val = fields[key];
    if (val != null && val !== '') {
      query.set(key, String(val));
    }
  });
  return query.toString();
}

/**
 * 与 Result.reStore 一致的首页 URL，额外带上 roleName / productId / cpProductId
 * @param {PayFailHomeContext} ctx
 * @returns {string}
 */
function buildPayFailHomePath(ctx = {}, extraQuery = {}) {
  const query = new URLSearchParams(buildPayFailHomeQuery(ctx));
  Object.keys(extraQuery).forEach((key) => {
    const val = extraQuery[key];
    if (val != null && val !== '') {
      query.set(key, String(val));
    }
  });
  const suffix = query.toString() ? `?${query.toString()}` : '';
  if (process.env.NODE_ENV !== 'production') {
    return `https://gabres-test.${mainHost}/officialTopupNew.html${suffix}`;
  }
  return `https://pay.${mainHost}/platform/officialTopupNew.html${suffix}`;
}

export function buildPayFailHomeUrl(ctx = {}) {
  return buildPayFailHomePath(ctx);
}

/**
 * 切换支付方式：回支付页首页并进入收银台
 * - 非购物车：URL 带 productId/cpProductId，Home 自动选品开收银台
 * - 购物车：URL 带 payResume=cartSwitch + payCartOrderId=原订单号，
 *   Home 再 get_cart → 自动加购 → 开收银台（见 payFailCartResume.js）
 * @param {PayFailHomeContext} ctx
 * @returns {boolean} 是否已跳转
 */
export function redirectToPayHomeForSwitchPaytype(ctx = {}) {
  // 购物车：不依赖单品 productId，用失败订单号驱动 Home 恢复购物车
  if (ctx.isCart) {
    if (!ctx.gameId || !ctx.orderId) {
      console.warn('[PayFailTip] switch_paytype: missing gameId or orderId');
      return false;
    }
    const link = buildPayFailHomePath(ctx, {
      payResume: PAY_FAIL_RESUME_CART_SWITCH,
      // 原订单号（get_cart 用）；get_orders 轮询时另在末尾拼「1」
      payCartOrderId: ctx.orderId,
    });
    window.location.href = getGlobalGetGotoLink({ link });
    return true;
  }
  if (!ctx.gameId || !ctx.productId) {
    console.warn('[PayFailTip] switch_paytype: missing gameId or productId');
    return false;
  }

  const link = buildPayFailHomePath(ctx, {
    payResume: PAY_FAIL_RESUME_SWITCH_PAYTYPE,
  });
  window.location.href = getGlobalGetGotoLink({ link });
  return true;
}
